import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenantData } from '../context/TenantDataContext';
import { useCachedFetch } from '../hooks/useCachedFetch';
import { useFindingGroups, canonicalFindingKey } from '../hooks/useFindingGroups';
import { isSettledNote } from '../utils/decisionStatus';
import LoadingState, { SyncNote } from '../components/LoadingState';
import { areaTabLabel } from '../data/areaLabels';
import { FRAMEWORKS, buildClauses, clauseStats } from '../data/complianceModel';
import { effectiveStatus, itemsByIdFrom } from '../utils/policyDisplay';
import { freshnessOf, useSyncStatus } from '../utils/freshness';
import FreshnessChip from '../components/FreshnessChip';
import './styles.css';
import './Oversigt.css';



// How the Secure Score has moved: compares today's score with the daily
// snapshot closest to a week ago (the backend keeps one per day). Returns
// null when there is no earlier snapshot to compare with — never guessed.
function scoreChange(scores, currentPct) {
  if (currentPct == null || !Array.isArray(scores)) return null;
  const today = new Date().toISOString().slice(0, 10);
  const earlier = scores.filter((s) => s.date < today);
  if (earlier.length === 0) return null;
  const target = Date.now() - 7 * 86400000;
  const base = earlier.reduce((best, s) => (Math.abs(new Date(s.date).getTime() - target) < Math.abs(new Date(best.date).getTime() - target) ? s : best));
  const ageDays = Math.round((Date.now() - new Date(base.date).getTime()) / 86400000);
  const label = ageDays >= 6 && ageDays <= 8 ? 'siden sidste uge' : `siden ${new Date(base.date).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })}`;
  return { delta: currentPct - base.secureScorePct, from: base.secureScorePct, label };
}

// "I dag" / "I går" / "3 dage siden" for a calendar date (the history timeline keeps dates, not times).
function dayLabel(dateStr) {
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days <= 0) return 'I dag';
  return days === 1 ? 'I går' : `${days} dage siden`;
}

function reviewSoonCount(notes) {
  const limit = Date.now() + 30 * 86400000;
  return notes.filter((n) => n.reviewDate && new Date(n.reviewDate).getTime() <= limit).length;
}

// Microsoft sets no target of its own for the Secure Score: this is NordComply's recommended level, marked on the ring.
const RECOMMENDED_SCORE = 70;
const ringPoint = (pct, r) => { const th = (pct * 3.6 * Math.PI) / 180; return [95 + r * Math.cos(th), 95 + r * Math.sin(th)]; };

export default function Oversigt() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tenant, secureScore, mfa, ca, devices, membership, applications, security, loading } = useTenantData();
  const sync = useSyncStatus(tenant?.tenantId);
  const { data: risks, loading: risksLoading } = useCachedFetch(tenant ? `/api/identity-risks?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const { data: insightsData, loading: insightsLoading } = useCachedFetch(tenant ? `/api/exchange-insights?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const insights = insightsData?.error ? null : insightsData;
  const { data: licensesResult, loading: licensesLoading } = useCachedFetch(tenant ? `/api/licenses?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const licenses = Array.isArray(licensesResult?.licenses) ? licensesResult.licenses : null;
  const { data: sitesResult, loading: sitesLoading } = useCachedFetch(tenant ? `/api/sharepoint-sites?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const sites = Array.isArray(sitesResult?.sites) ? sitesResult.sites : null;
  const { data: groupsResult, loading: groupsLoading } = useCachedFetch(tenant ? `/api/groups?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const workspaces = Array.isArray(groupsResult?.workspaces) ? groupsResult.workspaces : null;
  // The finding count is only right once every source it is built from has answered. Arbejdskø is
  // opened long after these have loaded, so it always shows the final number; this page is often
  // the first thing opened, and used to show whatever had arrived so far, a number too low.
  const findingsReady = !risksLoading && !insightsLoading && !licensesLoading && !sitesLoading && !groupsLoading;
  const { data: notesResult } = useCachedFetch(tenant ? `/api/audit-notes?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const notes = Array.isArray(notesResult?.notes) ? notesResult.notes : [];
  const { data: historyData } = useCachedFetch(tenant ? `/api/history?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  // Same three fetches Compliance itself makes, for the same byFw/coverage computation — the
  // Frameworks tile is a smaller read of the exact same data, not a separate approximation.
  const inv = useCachedFetch(tenant ? `/api/policy-inventory?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const ctl = useCachedFetch(tenant ? `/api/controls?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const scope = useCachedFetch(tenant ? `/api/policy-scope?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const itemsById = useMemo(() => itemsByIdFrom(inv.data), [inv.data]);
  const fwControls = useMemo(
    () => (ctl.data && !ctl.data.error ? ctl.data.controls.map((c) => ({ ...c, eff: effectiveStatus(c, itemsById, scope) })) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctl.data, itemsById, scope.data],
  );
  const fwList = useMemo(
    () => FRAMEWORKS.map((f) => ({ ...f, coverage: clauseStats(buildClauses(fwControls, f.key)).coverage })),
    [fwControls],
  );

  // The exact same finding logic Arbejdskø itself uses — not a separate,
  // approximate count that can silently drift from what Arbejdskø shows
  // for the same tenant (see useFindingGroups.js).
  const { nonEmptyGroups } = useFindingGroups({ mfa, risks, ca, devices, membership, insights, applications, security, sites, workspaces, licenses });

  if (loading) return <LoadingState syncNote />;

  const firstName = (user?.name || '').split(' ')[0] || '';
  // One object is one finding (see consolidateGroups). "Kræver handling" counts the objects that need action, each once, and what
  // has been decided (accepted, false positive, compensating control) no longer counts. A "Skal rettes" decision still does.
  const latestNote = new Map();
  [...notes].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)).forEach((n) => {
    if (n.itemKey && n.findingKey && n.decisionType) latestNote.set(n.itemKey + '::' + canonicalFindingKey(n.findingKey), n);
  });
  const live = nonEmptyGroups
    .filter((g) => g.tone === 'bad' || g.tone === 'warn')
    .map((g) => {
      const open = g.items.filter((i) => !isSettledNote(latestNote.get(i.id + '::' + g.key)));
      return { ...g, open, decided: g.items.length - open.length };
    });
  const badCount = live.filter((g) => g.tone === 'bad').reduce((sum, g) => sum + g.open.length, 0);
  const warnCount = live.filter((g) => g.tone === 'warn').reduce((sum, g) => sum + g.open.length, 0);
  const decidedCount = live.reduce((sum, g) => sum + g.decided, 0);
  // Tag fat her: the findings that matter most, red before orange, and within each the ones touching the most objects.
  const topGroups = live.filter((g) => g.open.length > 0)
    .sort((a, b) => (a.tone === b.tone ? b.open.length - a.open.length : a.tone === 'bad' ? -1 : 1))
    .slice(0, 5);
  // Fund pr. område: the same objects, counted the same way.
  const areaTotals = {};
  live.forEach((g) => {
    if (g.open.length === 0) return;
    const e = areaTotals[g.area] || (areaTotals[g.area] = { bad: 0, warn: 0 });
    e[g.tone] += g.open.length;
  });
  const areaRows = Object.entries(areaTotals)
    .map(([area, c]) => ({ area, ...c, total: c.bad + c.warn }))
    .sort((a, b) => b.total - a.total);
  const areaMax = areaRows[0]?.total || 1;
  const recentChanges = Array.isArray(historyData?.entries) ? historyData.entries.slice(0, 4) : [];
  const openWorkQueue = (query) => navigate(`/tenant/${tenantId}/work-queue${query}`);
  // Microsoft's own figure, with its two decimals (62,58 %), the same one the Microsoft portal shows.
  const scorePct = secureScore ? Math.round((secureScore.currentScore / secureScore.maxScore) * 10000) / 100 : null;
  const scorePctText = scorePct != null ? scorePct.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : null;
  const scoreColor = scorePct == null ? 'var(--border-strong)' : scorePct < 50 ? 'var(--danger)' : scorePct < 80 ? 'var(--warning)' : 'var(--success)';
  // The history keeps whole percentages, so the change since last week is measured in whole percentages too.
  const change = scoreChange(historyData?.scores, scorePct != null ? Math.round(scorePct) : null);
  const reviewSoon = reviewSoonCount(notes);

  const freshness = freshnessOf(sync.routes, null, sync.now);
  // Each part comes in once, a little after the one before; the last one is in place well within two seconds.
  const rvs = (d) => ({ '--d': d });

  return (
    <div className="ov2">
      <header className="ov2-head ov2-rv" style={{ '--d': 0 }}>
        <div>
          <div className="ov2-kick">Overblik</div>
          <h1 className="ov2-h1">Hej <span>{firstName}</span>.</h1>
          <p className="ov2-sub">NordComply samler jeres Microsoft 365-sikkerhed ét sted, forklarer hvad der betyder noget, og gemmer beslutninger som dokumentation.</p>
        </div>
        <FreshnessChip tenantName={tenant?.name} freshness={freshness} />
      </header>
      {!findingsReady && <SyncNote />}

      <section className="ov2-hero">
        <div className="ov2-card ov2-rv" style={rvs(100)}>
          <div className="ov2-eyebrow">Kræver handling</div>
          <div className="ov2-big ov2-big--bad"><b>{findingsReady ? badCount : '…'}</b><span>fund kræver handling</span></div>
          <div className="ov2-chips">
            <span className="ov2-chip ov2-chip--warn"><u />{findingsReady ? warnCount : '…'} bør gennemgås</span>
            {findingsReady && decidedCount > 0 && <span className="ov2-chip"><u style={{ background: 'var(--success)' }} />{decidedCount} besluttet</span>}
          </div>
          <p className="ov2-hint">Det store tal er det, der haster. Fund, I har besluttet (accepteret, falsk positiv eller kompenseret), tæller ikke med.</p>
        </div>

        <div className="ov2-card ov2-score ov2-rv" style={rvs(180)}>
          <div className="ov2-eyebrow">Sikkerhedsscore</div>
          <div className="ov2-gauge" style={{ '--circ': 2 * Math.PI * 80, '--off': 2 * Math.PI * 80 * (1 - (scorePct ?? 0) / 100) }}>
            <svg viewBox="0 0 190 190">
              <circle className="trk" cx="95" cy="95" r="80" fill="none" strokeWidth="14" />
              {scorePct != null && scorePct < RECOMMENDED_SCORE && (
                <circle cx="95" cy="95" r="80" fill="none" strokeWidth="14" stroke="var(--action)" strokeOpacity=".28" strokeDasharray={`${((RECOMMENDED_SCORE - scorePct) / 100) * 2 * Math.PI * 80} ${2 * Math.PI * 80}`} strokeDashoffset={-(scorePct / 100) * 2 * Math.PI * 80} />
              )}
              <circle className="arc" cx="95" cy="95" r="80" fill="none" strokeWidth="14" style={{ stroke: scoreColor }} />
              {scorePct != null && <circle cx={ringPoint(RECOMMENDED_SCORE, 80)[0]} cy={ringPoint(RECOMMENDED_SCORE, 80)[1]} r="9" fill="var(--card-lift)" stroke="var(--action)" strokeWidth="3.5" />}
            </svg>
            <div className="ov2-gauge__num"><b>{scorePctText != null ? `${scorePctText}%` : '—'}</b><small>Microsoft Secure Score</small></div>
          </div>
          {scorePct != null && (
            <p className="ov2-gauge__lg"><i />{scorePct < RECOMMENDED_SCORE ? `Mangler ${Math.round(RECOMMENDED_SCORE - scorePct)} point til anbefalet niveau: ${RECOMMENDED_SCORE} %` : `Over det anbefalede niveau: ${RECOMMENDED_SCORE} %`}</p>
          )}
          {change && (
            <span className={`ov2-delta ov2-delta--${change.delta > 0 ? 'up' : change.delta < 0 ? 'down' : 'flat'}`} title={`Fra ${change.from}% til ${scorePctText}% ${change.label}`}>
              {change.delta > 0 ? `↑ +${change.delta}` : change.delta < 0 ? `↓ −${Math.abs(change.delta)}` : '→ 0'} {change.label}
            </span>
          )}
        </div>
      </section>

      {findingsReady && (
        <>
        <section className="ov2-row">
          <div className="ov2-card ov2-rv" style={rvs(0)}>
            <div className="ov2-h3"><h3>Tag fat her</h3>{badCount + warnCount > 0 && <button type="button" className="ov2-link" onClick={() => openWorkQueue('')}>Åbn Gennemgang →</button>}</div>
            {topGroups.length === 0 ? <p className="ov2-empty">Ingen fund lige nu.</p> : topGroups.map((g) => (
              <button type="button" className="ov2-find" key={g.key} onClick={() => openWorkQueue(`?area=${g.area}&finding=${encodeURIComponent(g.key)}`)}>
                <u className={g.tone === 'bad' ? 'bad' : 'warn'} />
                <span><b>{g.title}</b><small>{g.hint}</small></span>
                <span className="area">{areaTabLabel(g.area)}</span>
                <span className="n">{g.open.length}</span>
              </button>
            ))}
          </div>

          <div className="ov2-stack">
            <div className="ov2-card ov2-rv" style={rvs(80)}>
              <div className="ov2-h3"><h3>Fund pr. område</h3></div>
              <div className="ov2-bars">
                {areaRows.map((r, i) => (
                  <button type="button" className="ov2-bar" key={r.area} onClick={() => openWorkQueue(`?area=${r.area}`)}>
                    <span>{areaTabLabel(r.area)}</span>
                    <span className="t">
                      <i className="bad" style={{ '--w': `${(r.bad / areaMax) * 100}%`, '--d': 260 + i * 60 }} />
                      <i className="warn" style={{ '--w': `${(r.warn / areaMax) * 100}%`, '--d': 300 + i * 60 }} />
                    </span>
                    <b>{r.total}</b>
                  </button>
                ))}
              </div>
              <div className="ov2-legend"><span><u style={{ background: 'var(--danger)' }} />Kræver handling</span><span><u style={{ background: 'var(--warning)' }} />Bør gennemgås</span></div>
            </div>

            <div className="ov2-card ov2-rv" style={rvs(160)}>
              <div className="ov2-h3"><h3>Frameworks</h3><button type="button" className="ov2-link" onClick={() => navigate(`/tenant/${tenantId}/compliance`)}>Se alle →</button></div>
              <div className="ov2-fw">
                <div>
                  <b>Secure Score</b>
                  <span className="p">{scorePct == null ? '—' : <>{scorePctText}<small>%</small></>}</span>
                  <span className="m"><i style={{ '--w': `${scorePct ?? 0}%`, '--d': 380 }} /></span>
                </div>
                {fwList.map((f, i) => (
                  <div key={f.key}>
                    <b>{f.label}</b>
                    <span className="p">{f.coverage === null ? '—' : <>{f.coverage}<small>%</small></>}</span>
                    <span className="m"><i style={{ '--w': `${f.coverage ?? 0}%`, '--d': 450 + i * 70 }} /></span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="ov2-row">
        <div className="ov2-card ov2-rv" style={rvs(240)}>
          <div className="ov2-h3"><h3>Seneste ændringer</h3><button type="button" className="ov2-link" onClick={() => navigate(`/tenant/${tenantId}/history`)}>Se hele historikken →</button></div>
          {recentChanges.length === 0 ? (
            <p className="ov2-empty">Ingen ændringer endnu. De vises, når der er mindst to dages målinger.</p>
          ) : (
            <div className="ov2-tl">
              {recentChanges.map((c, i) => (
                <div key={`${c.date}-${i}`}>
                  <span className={`ic ${c.tone}`}>{c.tone === 'ok' ? '✓' : '!'}</span>
                  <span>{c.text}</span>
                  <time>{dayLabel(c.date)}</time>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="ov2-card ov2-rv" style={rvs(320)}>
          <div className="ov2-h3"><h3>Dokumentation</h3><button type="button" className="ov2-link" onClick={() => navigate(`/tenant/${tenantId}/decisions`)}>Se beslutninger →</button></div>
          <button type="button" className="ov2-doc" onClick={() => navigate(`/tenant/${tenantId}/decisions`)}>
            <b>{notes.length}</b><span>dokumenterede beslutninger</span>
          </button>
          {reviewSoon > 0 && <div className="ov2-chips"><span className="ov2-chip ov2-chip--warn"><u />{reviewSoon} skal revurderes</span></div>}
        </div>
        </section>
        </>
      )}
    </div>
  );
}
